import { PluginListenerHandle, registerPlugin } from '@capacitor/core';
import { TReadpowerUpdateInfo, TRFIDConnectionInfo, TScanModes } from '../rfid-tag-reader.service';
import { TRfidDevice } from '@monorepo/types';

/**
 * @description: Capacitor Custom Native Plugin,
 * this is an Interface class defining the supported methods
 * you can find more details https://capacitorjs.com/docs/android/custom-code
 *
 */
export interface RfidReaderPlugin {
  /**
   * @description helps to connect to vendor based rfid device
   * @param deviceDetails - takes device details - vendor, deviceId, readPower, deviceName, and scanMode as an argument
   * @returns connection status => "SUCCESS" | "FAILURE"
   */
  connectToVendorBasedRfidDevice(options: {
    vendor: TRfidDevice['vendor'];
    deviceId: TRfidDevice['deviceId'];
    readPower: TRfidDevice['readPower'];
    deviceName: TRfidDevice['deviceName'];
    scanMode: TScanModes;
  }): Promise<{ status: 'SUCCESS' | 'FAILURE' }>;

  /**
   * @description helps to start the tag read of Rfid Device.
   * returns status or error, if unable to read data.
   * @returns status => true / false
   */
  startRFIDRead(): Promise<{ status: string }>;

  /**
   * @description helps to stop the tag read of Rfid Device.
   * returns status or error.
   * @returns status => true or false
   */
  stopRFIDRead(): Promise<{ status: string }>;

  /**
   * @description helps to update the Rfid scan mode from single to multi mode. By Default bulk scan mode
   * returns status or error, based on Mode toggle sucess of failure.
   * @param scanMode - single_tag or multi_tag.
   * @returns status => update status
   */
  updateScanMode(options: { scanMode: string }): Promise<{ status: boolean }>;

  /**
   * @description helps to update the readpower of the rfid device
   * @param options - readpower
   */
  updateReadpower(options: { readpower: number }): void;

  /**
   * @description helps to disconnect from Rfid Device.
   * returns status or error, if unable to disconnect.
   * @returns status => true / false
   */
  disconnectRFIDDevice(): Promise<{ status: boolean }>;

  /**
   * @description listen to Rfid tag data update.
   * return list of tags datas.
   * @returns tagData - list of Rfid Tags
   */
  addListener(
    eventName: 'rfidTagDataUpdate',
    listenerFunc: (info: { status: string; tagList: string[] }) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;

  /**
   * @description listen to Rfid device connection state update.
   * return connectionStatus value.
   * @returns connectionStatus - CONNECTED / DISCONNECTED
   */
  addListener(
    eventName: 'rfidConnectionUpdate',
    listenerFunc: (info: TRFIDConnectionInfo) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;

  /**
   * @description listens to readpower update state
   * @returns old readpower value with new updated readpower value
   */
  addListener(
    eventName: 'readpowerUpdate',
    listenerFunc: (info: TReadpowerUpdateInfo) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
}

const RfidReader = registerPlugin<RfidReaderPlugin>('RFIDReader');

export default RfidReader;

/* Example code to addListener and remove Listener

const myAppUpdaterPlugEventListener = await RfidReader.addListener(
  '<myPluginEvent>',
  (info: any) => {
    console.log('<myPluginEvent> was fired');
  },
);

myAppUpdaterPlugEventListener.remove();
*/
